import { ReactNode } from 'react'
import styled from 'styled-components'
import { H, P, Wrapper } from '@farewill/ui'
import { BORDER, COLOR, FONT, GTR } from '@farewill/ui/tokens'

import {
  FuneralPlanCurrentPlan,
  FuneralPlanPlanPart,
} from 'lib/models/funeral-plan'

import { formatDay, formatPence } from './payment-shared'

const StyledCard = styled(Wrapper)`
  padding: ${GTR.M};
  background: ${COLOR.ACCENT.PRIMARY_20};
  border: 1px solid ${COLOR.ACCENT.PRIMARY};
  border-radius: ${BORDER.RADIUS.L};
`

/** One card, or a card for the plan and one for a celebration paid for
 * differently, side by side */
const StyledCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
  gap: ${GTR.M};
`

const StyledCardHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${GTR.S};
`

/** As tall as the plan name's line, so the buttons sit over it rather than
 * making the header taller than it is without them */
const StyledActions = styled.div`
  display: flex;
  gap: ${GTR.XS};
  align-items: center;
  height: calc(${FONT.SIZE.M} * 1.5);
`

const StyledMonthly = styled(P)`
  margin: ${GTR.S} 0 ${GTR.XXS};
`

const StyledPaidInFull = styled.span`
  color: ${COLOR.STATE.SUCCESS_120};
  font-weight: ${FONT.WEIGHT.BOLD};
`

const StyledAmount = styled.span`
  font-size: ${FONT.SIZE.L};
  font-weight: ${FONT.WEIGHT.BOLD};
  margin-right: ${GTR.XXS};
`

const StyledMeta = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: ${GTR.XS};
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: ${FONT.SIZE.XS};

  li + li::before {
    content: '•';
    margin-right: ${GTR.XS};
  }
`

const StyledFigures = styled.dl`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${GTR.M};
  margin: ${GTR.L} 0 0;
`

/** Sized as the design has them, with a rule between each */
const StyledFigure = styled.div`
  & + & {
    padding-left: ${GTR.M};
    border-left: 1px solid ${COLOR.GREY.LIGHT};
  }

  dt {
    font-size: ${FONT.SIZE.M};
    color: ${COLOR.GREY.DARK};
  }

  dd {
    margin: ${GTR.XS} 0 0;
    font-size: ${FONT.SIZE.L};
    font-weight: ${FONT.WEIGHT.MEDIUM};
    color: ${COLOR.BLACK};
  }
`

type Props = {
  currentPlan: FuneralPlanCurrentPlan
  figures: {
    totalPriceInPence: number
    totalPaidInPence: number
    leftToPayInPence: number
    unpaidInvoicesInPence: number
  }
  cancellationPending: boolean
  /** The plan has been cancelled, so it has no term or payments to show */
  cancelled?: boolean
  amountToRefundInPence: number
  /** Buttons for the card's header, such as paying the balance. On the
   * instalments card when a celebration has a card of its own. */
  actions?: ReactNode
}

type CardProps = Omit<Props, 'currentPlan' | 'figures'> & {
  part: FuneralPlanPlanPart
}

/** The plan, or a celebration paid for differently from it. */
const PlanCard = ({
  part,
  cancellationPending,
  cancelled,
  amountToRefundInPence,
  actions,
}: CardProps): React.ReactElement => {
  const { name, instalments } = part

  return (
    <StyledCard data-testid="plan-card">
      <StyledCardHeader>
        <P strong>{name}</P>
        {actions && <StyledActions>{actions}</StyledActions>}
      </StyledCardHeader>

      {/* the plan is cancelled on the Cancel click, but its cancellation is
          in progress until every refund is made */}
      {cancellationPending && (
        <>
          <StyledMonthly>Cancellation in progress</StyledMonthly>
          <P margin={[GTR.XS, 0, 0]}>
            Refund due {formatPence(amountToRefundInPence)}
          </P>
        </>
      )}

      {cancelled && !cancellationPending && (
        <StyledMonthly>Cancelled</StyledMonthly>
      )}

      {!cancelled && !cancellationPending && instalments && (
        <>
          <StyledMonthly>
            <StyledAmount>
              {formatPence(instalments.monthlyPaymentInPence)}
            </StyledAmount>
            <span>a month</span>
          </StyledMonthly>
          <StyledMeta>
            <li>{instalments.termMonths} month term</li>
            <li>
              {instalments.monthsLeft}{' '}
              {instalments.monthsLeft === 1 ? 'month' : 'months'} left
            </li>
            {instalments.nextPaymentDueOn && (
              <li>
                Next payment due {formatDay(instalments.nextPaymentDueOn)}
              </li>
            )}
          </StyledMeta>
        </>
      )}

      {!cancelled && !cancellationPending && !instalments && (
        <StyledMonthly>
          {part.paidInFullOn ? (
            <StyledPaidInFull>Paid in full</StyledPaidInFull>
          ) : (
            `${formatPence(part.stillToPayInPence)} still to pay`
          )}
        </StyledMonthly>
      )}
    </StyledCard>
  )
}

/** The plan in place at the top of the payment page, with its figures. */
const PaymentCurrentPlan = ({
  currentPlan,
  figures,
  actions,
  ...state
}: Props): React.ReactElement => {
  const { celebration, ...plan } = currentPlan
  const figureRows: [label: string, valueInPence: number][] = [
    ['Plan total price', figures.totalPriceInPence],
    ['Paid so far', figures.totalPaidInPence],
    // a cancelled plan has nothing left to pay, so while its cancellation is
    // in progress the refund due is what's left; more paid than is owed is
    // a refund too
    state.cancellationPending
      ? ['Still to refund', state.amountToRefundInPence]
      : figures.leftToPayInPence < 0
        ? ['Still to refund', -figures.leftToPayInPence]
        : ['Still to pay', figures.leftToPayInPence],
    ['Unpaid invoices', figures.unpaidInvoicesInPence],
  ]
  /** Paying the balance is for the instalments */
  const actionsOnCelebration = celebration?.paymentType === 'Instalments'

  return (
    <Wrapper data-testid="current-plan">
      <H tag="h3" size="S">
        Current plan
      </H>
      <StyledCards>
        <PlanCard
          part={plan}
          actions={actionsOnCelebration ? undefined : actions}
          {...state}
        />
        {celebration && (
          <PlanCard
            part={celebration}
            actions={actionsOnCelebration ? actions : undefined}
            {...state}
          />
        )}
      </StyledCards>

      <StyledFigures>
        {figureRows.map(([label, valueInPence]) => (
          <StyledFigure key={label} data-testid="plan-figure">
            <dt>{label}</dt>
            <dd>{formatPence(valueInPence)}</dd>
          </StyledFigure>
        ))}
      </StyledFigures>
    </Wrapper>
  )
}

export default PaymentCurrentPlan
